import React, { useEffect, useRef, useState } from "react"
import {
  Euler,
  WebGLRenderer,
  Scene,
  PerspectiveCamera,
  AmbientLight,
  Mesh,
  Color,
  ShaderMaterial,
  AdditiveBlending,
  Float32Attribute,
} from "three"
import ContourPoints from "./src/ContourPoints"
import { contourData } from "./contourData.js"
import * as THREE from "three"
import { lineFragmentShader } from "./src/line.fragment.shader.js"
import { lineVertexShader } from "./src/line.vertex.shader.js"
import { storyblokEditable } from "gatsby-source-storyblok"
import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { ScrollToPlugin } from "gsap/ScrollToPlugin"

////////////////////////////////////////////////////////////////
//show webgl rendering FPS stats?                             //
const showStats = false //
//re-generate the d3 force directed graph data for section 2? //
const generateGraphData = false //
////////////////////////////////////////////////////////////////

const PlatformAnimation = ({ blok }) => {
  const [scene, setScene] = useState(null)
  const [renderer, setRenderer] = useState(null)
  const [camera, setCamera] = useState(null)

  const sectionId = `platform-animation-${blok._uid}`;
  const sectionRef = useRef();

  useEffect(() => {
    if (typeof window == "undefined") { 
      return;
    }

    window.THREE = THREE
    require("three/examples/js/loaders/OBJLoader")

    const scene = new Scene()
    const camera = new PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      100000
    )

    const renderer = new WebGLRenderer({ antialias: true })
    renderer.setClearColor("#000000")
    renderer.setSize(window.innerWidth, window.innerHeight)
    document.getElementById(sectionId).appendChild(renderer.domElement)
    renderer.domElement.classList.add("glslRenderer")
    renderer.render(scene, camera)

    camera.updateProjectionMatrix()

    // adding ambient light
    const light = new AmbientLight(0x404040, 50)
    scene.add(light)

    //uniforms for the line shader
    const uniforms = {
      amplitude: { value: 5.0 },
      opacity: { value: 0.2 },
      color: { value: new Color(0xffffff) },
      posZ: { value: 0.0 },
      globalOpacity: { value: 0.0 },
      mode: { value: 2.0 },
    }

    // IMPORTING OBJ MODEL
    const loader = new THREE.OBJLoader()
    loader.load("/edges03.obj", object => {
      const shaderMaterial = new ShaderMaterial({
        uniforms: uniforms,
        vertexShader: lineVertexShader,
        fragmentShader: lineFragmentShader,
        blending: AdditiveBlending,
        depthTest: false,
        transparent: true,
      })

      object.name = "objcontours"
      object.scale.set(0.1, 0.1, 0.1)
      object.setRotationFromEuler(new Euler(0.3, Math.PI + 0.3, -1))
      object.position.z = -30
      object.position.x = 150
      object.position.y = -105
      object.traverse(function (child) {
        if (child instanceof Mesh) {
          let colors = []
          for (
            let i = 0;
            i < child.geometry.attributes.position.array.length;
            i++
          ) {
            colors.push(1) //white it out
          }

          child.geometry.addAttribute(
            "customColor",
            new Float32Attribute(colors, 3)
          )
          child.geometry.attributes.customColor.needsUpdate = true
          child.geometry.attributes.position.needsUpdate = true
          child.material = shaderMaterial
        }
      })
      scene.add(object)

      setScene(scene)
      setRenderer(renderer)
      setCamera(camera)
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [])

  useEffect(() => {
    if (typeof renderer?.domElement === 'undefined') {
      return
    }

    // Sticky canvas
    const ctx = gsap.context(self => {
      gsap.registerPlugin(ScrollTrigger, ScrollToPlugin)
      const page1 = sectionRef.current.querySelector('#page1');
      const page2 = sectionRef.current.querySelector('#page2');
      const page3 = sectionRef.current.querySelector('#page3');
      const pageGap = sectionRef.current.querySelector('#gap-page');
      ScrollTrigger.create({
        trigger: renderer.domElement,
        start: "top top",
        end: function(){return `+=${page1.offsetHeight + page2.offsetHeight + page3.offsetHeight + pageGap.offsetHeight - renderer.domElement.offsetHeight}px`},
        pin: true,
        // markers: { indent: 25 },
      })
    }, sectionRef) // <- Scope!
    return () => ctx.revert() // <- Cleanup!
  }, [renderer?.domElement])

  return (
    <section
      className="platform-animation content_block"
      {...storyblokEditable(blok)}
      key={blok._uid}
      ref={sectionRef}
      id={sectionId}
    >
      <ContourPoints
        renderer={renderer}
        scene={scene}
        camera={camera}
        data={contourData}
        showStats={showStats}
        generateGraphData={generateGraphData}
      />
    </section>
  )
}

export default PlatformAnimation
